import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WorkOrderConfig } from './types';

interface WorkOrderDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId: string;
  orderNumber: string;
  customer: string;
  location?: string;
  date?: string;
  config: WorkOrderConfig;
  isTKS0026?: boolean;
}

export function WorkOrderDetailDialog({
  open,
  onOpenChange,
  workOrderId,
  orderNumber,
  customer,
  location,
  date,
  config,
  isTKS0026,
}: WorkOrderDetailDialogProps) {
  const showInclusionFlags = !config.completeUnit;
  const retentionMixed =
    !!config.retentionLeft &&
    !!config.retentionRight &&
    config.retentionLeft !== config.retentionRight;
  const showFrameVariants = config.frameColorVaries;

  const sections: SectionSpec[] = (
    [
      {
        title: 'Dimensions',
        rows: [
          ['Width', config.width ? `${config.width}"` : ''],
          ['Height', config.height ? `${config.height}"` : ''],
        ],
      },
      {
        title: 'Housing',
        rows: [
          ['Type', config.housingType],
          ['Size', config.housingSize],
        ],
      },
      {
        title: 'Motor',
        rows: [
          ['Type', config.motorType],
          ['Drive', config.driveSide],
          ['Cord', config.powerCord],
        ],
      },
      {
        title: 'Fabric',
        rows: [
          ['Type', config.fabricType],
          ['Seam', config.seam],
          ['Height Adj', config.fabricHeightAdjustment],
          ['Rotate', config.rotateFabric],
          ['Zipper', config.zipperColor],
        ],
      },
      {
        title: 'Hem Bar',
        rows: [
          ['Type', config.hemBarType],
          ['Cap', config.hemBarCapColor],
          ['Pile', config.pileBrush],
        ],
      },
      {
        title: 'Retention',
        rows: retentionMixed
          ? [
              ['Left', config.retentionLeft],
              ['Right', config.retentionRight],
              ['Cap', config.retentionCapColor],
            ]
          : [
              ['Retention', config.retention || config.retentionLeft || config.retentionRight],
              ['Cap', config.retentionCapColor],
            ],
        bools: [['Cut Tracks', config.cutTracks]],
      },
      {
        title: showFrameVariants ? 'Frame Colors (Varies)' : 'Frame Color',
        rows: showFrameVariants
          ? [
              ['Frame', config.frameColor],
              ['End Cap', config.endCapFrameColor],
              ['Housing', config.housingFrameColor],
              ['Hem Bar', config.hemBarFrameColor],
              ['Retention', config.retentionFrameColor],
              ['Buildout', config.buildoutFrameColor],
            ]
          : [['Frame', config.frameColor]],
      },
      {
        title: 'Buildout',
        hide: !config.buildoutIncluded,
        rows: [
          ['Material', config.buildoutMaterial],
          ['Qty', config.buildoutQuantity],
          ['Length', config.buildoutLength],
        ],
      },
      {
        title: 'Extras',
        rows: [
          ['Clear Window', config.clearWindow ? config.clearWindowAmount : ''],
          ['Window Unit', config.clearWindow ? config.clearWindowUnit : ''],
        ],
        bools: [
          ['Aluminum End Caps', !!config.endCapFrameColor],
          ['CF Tube', config.cfTube],
          ['Pre-Drilled', config.preDrilledTracks],
          ['Clear Window', config.clearWindow],
        ],
      },
      {
        title: 'Scheduling',
        rows: [
          ['Priority', config.schedulePriority],
          ['Frame', config.scheduleFrame],
          ['Fabric', config.scheduleFabric],
        ],
      },
    ] as SectionSpec[]
  ).filter((s) => !s.hide && sectionHasContent(s));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-5 pt-4 pb-2.5">
          <DialogTitle className="text-base flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
            <span className="font-semibold text-slate-900">{workOrderId}</span>
            {config.unitName && (
              <span className="text-slate-500 font-normal text-sm">· {config.unitName}</span>
            )}
            {isTKS0026 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
                TKS-0026
              </span>
            )}
          </DialogTitle>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2.5 gap-y-0.5 mt-0.5">
            <span>
              SO <span className="text-slate-700 font-medium">{orderNumber}</span>
            </span>
            <span>·</span>
            <span>{customer}</span>
            {location && (
              <>
                <span>·</span>
                <span>{location}</span>
              </>
            )}
            {date && (
              <>
                <span>·</span>
                <span>{date}</span>
              </>
            )}
            {config.woStatus && (
              <>
                <span>·</span>
                <span className="font-medium text-slate-700">{config.woStatus}</span>
              </>
            )}
          </div>
        </DialogHeader>

        <div className="px-4 pt-2 pb-4">
          {showInclusionFlags && (
            <div className="mb-3 pb-3 border-b border-slate-100 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1 self-center">
                Includes
              </span>
              {[
                ['Housing', config.includesHousing],
                ['Side Tracks', config.includesSideTracks],
                ['Hem Bar', config.includesHemBar],
                ['Fabric', config.includesFabric],
                ['Motor Tube', config.includesMotorTube],
                ['Buildout', config.buildoutIncluded],
              ].map(([label, on]) => (
                <BoolChip key={String(label)} label={String(label)} value={!!on} />
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-3">
            {sections.map((s) => (
              <SectionBlock key={s.title} section={s} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface SectionSpec {
  title: string;
  rows: Array<[string, string]>;
  bools?: Array<[string, boolean]>;
  hide?: boolean;
}

function sectionHasContent(s: SectionSpec): boolean {
  const anyRow = s.rows.some(([, v]) => !!v && String(v).trim() !== '');
  const anyBool = (s.bools ?? []).some(([, v]) => v);
  return anyRow || anyBool;
}

function SectionBlock({ section }: { section: SectionSpec }) {
  const visibleRows = section.rows.filter(([, v]) => !!v && String(v).trim() !== '');
  const visibleBools = (section.bools ?? []).filter(([, v]) => v);
  return (
    <section className="min-w-0">
      <h3 className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        {section.title}
      </h3>
      <dl className="space-y-0.5">
        {visibleRows.map(([label, value]) => (
          <div key={label} className="flex items-baseline gap-1.5 text-xs min-w-0">
            <dt className="text-slate-400 shrink-0">{label}</dt>
            <dd className="text-slate-900 font-semibold truncate min-w-0" title={value}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {visibleBools.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {visibleBools.map(([label]) => (
            <BoolChip key={label} label={label} value={true} />
          ))}
        </div>
      )}
    </section>
  );
}

function BoolChip({ label, value }: { label: string; value: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border',
        value
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-slate-50 text-slate-400 border-slate-200 line-through'
      )}
    >
      {value && <Check className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}
